// @spec INC-STR-050
// @spec INC-STR-051
// @spec INC-STR-052
// @spec INC-STR-053
'use client';

import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { ArrowLeft, Settings2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CategoriesCard } from '@/features/incidents/admin/categories';
import { NotificationTemplatesCard } from '@/features/incidents/admin/notification-templates';
import { RegulatoryConfigCard } from '@/features/incidents/admin/regulatory-config';
import { SLAConfigCard } from '@/features/incidents/admin/sla-config';
import { WorkflowSettingsCard } from '@/features/incidents/settings';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/incidents/settings')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.incidentSettings'),
  }),
  component: IncidentSettingsLayout,
});

/**
 * Incident Settings layout/page.
 *
 * When at /incidents/settings exactly, renders the settings cards.
 * When at a child route (e.g., /incidents/settings/sla), renders child via Outlet.
 *
 * Access:
 * - All authenticated users can view
 * - Only org:admin or org:incident:admin roles can edit
 */
function IncidentSettingsLayout() {
  const matches = useRouterState({ select: (s) => s.matches });
  const isChildRoute = matches.some((m) => m.routeId.includes('/settings/'));

  if (isChildRoute) {
    return <Outlet />;
  }

  return <IncidentSettingsPage />;
}

function IncidentSettingsPage() {
  const { isAdmin } = useOrgRole();

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Header */}
        <header className="space-y-4">
          <div className="flex items-center gap-4">
            <Link to="/incidents">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Incidents
              </Button>
            </Link>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <Settings2 className="h-6 w-6 text-muted-foreground" />
              <h1 className="text-3xl font-semibold text-foreground">Incident Settings</h1>
            </div>
            <p className="mt-2 text-muted-foreground">
              Configure incident workflow, status requirements, and closure conditions for your
              organization.
            </p>
          </div>
        </header>

        {/* Settings Cards */}
        <div className="space-y-6">
          <WorkflowSettingsCard isAdmin={isAdmin} />
          <CategoriesCard isAdmin={isAdmin} />
          <NotificationTemplatesCard isAdmin={isAdmin} />
          <RegulatoryConfigCard isAdmin={isAdmin} />
          <SLAConfigCard isAdmin={isAdmin} />
        </div>
      </div>
    </div>
  );
}
